/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

const SCRIPT_LOAD_TIMEOUT_MS = 5000;

const loadedScripts = new Map<string, Promise<void>>();

/**
 * Lazily injects a `<script>` tag once per `src` and caches the resulting
 * promise, so the Facebook Pixel and TikTok Pixel adapters (Phase 4) each
 * load their vendor SDK exactly once regardless of how many events fire
 * before the script finishes loading. Never called from the initial head
 * payload — only on first event needing the SDK — per
 * docs/README-TRACKING-METRICS.md's "defer scripts until interaction /
 * consent" rule.
 *
 * Rejects (rather than hanging forever) on load failure or after
 * `SCRIPT_LOAD_TIMEOUT_MS`, so callers can no-op cleanly instead of the
 * adapter silently never sending events.
 */
export function loadScriptOnce(src: string): Promise<void> {
    const cached = loadedScripts.get(src);
    if (cached) {
        return cached;
    }

    const promise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        const timeoutId = setTimeout(() => {
            reject(new Error(`Script load timed out: ${src}`));
        }, SCRIPT_LOAD_TIMEOUT_MS);

        script.onload = () => {
            clearTimeout(timeoutId);
            resolve();
        };
        script.onerror = () => {
            clearTimeout(timeoutId);
            reject(new Error(`Script load failed: ${src}`));
        };

        document.head.appendChild(script);
    });

    // A failed load must not be cached — evict so a later event can retry
    // (e.g. a transient CDN blip shouldn't permanently disable the pixel for
    // the rest of the page session).
    promise.catch(() => loadedScripts.delete(src));

    loadedScripts.set(src, promise);
    return promise;
}

/** Exposed for testing — resets the module-level script cache. */
export function resetLoadedScripts(): void {
    loadedScripts.clear();
}
